package dev.lingvo.site.components.editor

import dev.lingvo.shared.EditorCapabilitiesDto
import dev.lingvo.shared.ProjectDetailDto
import dev.lingvo.shared.Role
import dev.lingvo.site.state.ProjectAuthority
import dev.lingvo.site.state.VerifiedProjectRoute

/** Browser-normalized editor authority for one verified project snapshot. */
internal data class ResolvedEditorCapability(
    val canWriteTranslations: Boolean,
    val canApproveTranslations: Boolean,
    /**
     * Whether this person may start a run that spends the workspace's credits.
     *
     * **Deliberately not defaulted, and deliberately not derived from the other two.** It is the
     * only one of the three the browser cannot infer: the server answers role >= ADMIN *and* the
     * credential's `mt:spend` scope, and the token half of that pair is invisible here. A default
     * would let a new call site draw a pre-translate button that 403s, which is the exact failure
     * this field exists to prevent — so every construction states it.
     */
    val canSpendCredits: Boolean,
) {
    init {
        require(!canApproveTranslations || canWriteTranslations) {
            "Translation approval requires translation write authority."
        }
    }

    companion object {
        val ReadOnly = ResolvedEditorCapability(
            canWriteTranslations = false,
            canApproveTranslations = false,
            canSpendCredits = false,
        )
    }
}

/**
 * Resolves the compatibility-safe editor policy.
 *
 * A present server value is authoritative because it also reflects credential scopes and reviewer
 * overrides. The exact project role is used only for an older response that omitted the value.
 */
internal fun resolveEditorCapability(
    capabilities: EditorCapabilitiesDto?,
    exactRole: Role?,
): ResolvedEditorCapability {
    if (capabilities != null) {
        val canWrite = capabilities.canWriteTranslations
        return ResolvedEditorCapability(
            canWriteTranslations = canWrite,
            canApproveTranslations = canWrite && capabilities.canApproveTranslations,
            // Mirrored, not conjoined with anything: spending and writing diverge in both
            // directions. A translator types all day and starts no runs; a `strings:write` CI token
            // writes thousands of strings and may buy none.
            canSpendCredits = capabilities.canSpendCredits,
        )
    }

    return when (exactRole) {
        Role.OWNER,
        Role.ADMIN,
        -> ResolvedEditorCapability(
            canWriteTranslations = true,
            canApproveTranslations = true,
            // The role half of the server's pair, which is all an older response gives us. The
            // scope half is unknowable here, so the control is offered and the route is the one
            // that refuses — the same trade the write and approve fallbacks already make.
            canSpendCredits = true,
        )

        Role.TRANSLATOR -> ResolvedEditorCapability(
            canWriteTranslations = true,
            canApproveTranslations = false,
            canSpendCredits = false,
        )

        Role.VIEWER,
        null,
        -> ResolvedEditorCapability.ReadOnly
    }
}

/** Project identity and capability published atomically to the editor. */
internal data class EditorBootstrap(
    val project: ProjectDetailDto,
    val capability: ResolvedEditorCapability,
    val canEditStructure: Boolean = false,
)

internal fun VerifiedProjectRoute.editorBootstrap(): EditorBootstrap = EditorBootstrap(
    project = project,
    capability = resolveEditorCapability(project.editorCapabilities, workspace.role),
    canEditStructure = ProjectAuthority(workspace.role).canEditStructure,
)
